import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useProtocol, useProtocolMutations } from '../../hooks/useProtocols';
import { 
  useProtocolCategories, 
  useProtocolTypes,
  useRoomTypes, 
  useLocationTypes,
  useFrequencyTypes,
  useTriggerEvents
} from '../../hooks/useNomenclatures';
import { useUserGroups } from '../../hooks/useUserGroups';
import { useActiveRoles } from '../../hooks/useRoles';
import { useAssignmentTypes, useShiftTypes } from '../../hooks/useTaskNomenclatures';
import { useRiskClassificationNomenclature } from '../../hooks/useRiskClassificationNomenclature';
import { useRiskMapData } from '../../hooks/useRiskMapData';
import { userService } from '../../services/firestoreService';
import { 
  organizationService,
  locationService,
  buildingService,
  floorService,
  departmentService,
  roomService,
  bedService,
  equipmentService,
  personnelService,
  clinicalStructureService
} from '../../services/firebaseService';
import {
  createEmptyScheduleRule,
  normalizeLegacyRule,
  RECURRENCE,
  WEEK_IN_MONTH,
  DAYS_OF_WEEK,
  formatTimeSlot,
  parseTimeSlot,
  isScheduledType,
  isTriggeredType,
  isRiskDrivenType,
  SCHEDULE_TYPE_RISK_DRIVEN
} from '../../utils/scheduleRuleShape';
import { Save, X, Plus, Trash2, Edit, Eye, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import ProtocolStepEditor from './ProtocolStepEditor';
import Modal from '../ui/Modal';
import { useNotifications } from '../NotificationSystem';
import './ProtocolForm.css';

// Sortable Step Item Component (t from useTranslation in parent)
const SortableStepItem = ({ step, onEdit, onDelete, t }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="step-item"
    >
      <div className="step-header">
        <div 
          className="step-info sortable-handle"
          {...attributes}
          {...listeners}
        >
          <span className="step-number">{step.order || step.stepNumber}</span>
          <span className="step-name">{step.name || (t ? t('protocols:step') + ' ' + (step.order || step.stepNumber) : `Step ${step.order || step.stepNumber}`)}</span>
          <span className="step-duration">{step.estimatedDuration}{t ? t('protocols:min') : 'min'}</span>
          <span className="step-actions-count">{t ? t('protocols:actionsCount', { count: step.actions?.length || 0 }) : (step.actions?.length || 0) + ' actions'}</span>
          {step.description && (
            <span className="step-description-inline">{step.description}</span>
          )}
        </div>
        <div className="step-actions">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onEdit(step);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="btn btn-edit"
            title={t ? t('common:editStep') : 'Edit Step'}
          >
            <Edit size={14} />
            {t ? t('common:edit') : 'Edit'}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete(step.id);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="btn btn-delete"
            title={t ? t('common:removeStep') : 'Remove Step'}
          >
            <Trash2 size={14} />
            {t ? t('common:remove') : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProtocolForm = ({ protocolId, organizationId, onSave, onCancel, onClose }) => {
  const { t } = useTranslation(['common', 'protocols']);
  const { notify } = useNotifications();
  const [formData, setFormData] = useState({
    // Basic Info
    protocol_code: '',
    name: '',
    description: '',
    category: '',
    protocolType: '',
    version: '1.0',
    status: 'draft',
    is_template: false, // Mark as template
    
    // Evidence Base
    evidence_base: {
      guidelines: [],
      standards: [],
      regulations: []
    },
    
    // Task generation at protocol level (when no step/action generates a task)
    generateTaskAtProtocolLevel: false,
    defaultTaskTemplateId: null,
    defaultTaskTemplateName: null,
    
    // Applicability - Dynamic rules
    applicabilityRules: [],
    applicablePersonnel: [],
    clinicalContexts: [],
    conditions: [], // Not used at protocol level (condition types are for supplies/elsewhere)
    
    // Personnel Assignment (NEW) - Now array for multiple entries
    assignmentRules: [],
    
    // Scheduling - Now array for multiple entries
    schedulingRules: [],
    
    // Steps
    steps: [],
    
    // Metadata
    isActive: true,
    requiresApproval: false,
    approvalRequired: false,

    // Phase 4: Structural risk classification (ORDIN 1761) — which criticality level this protocol applies to
    structuralCriticalityLevelId: null,
    structuralCriticalityLevelLabel: null,
    // PRD 5.5: Filter applicability entity picker by protocol risk level (saved with protocol)
    filterByProtocolRiskLevel: true,
    // Quiz generation (PRD: quiz_generation_mechanism_prd.md) — Învățare & Evaluare
    learningAssessment: {
      questionCount: 10,
      passThresholdPercent: 70,
      cooldownMinutes: 60,
      generateQuiz: false
    }
  });

  const [activeStep, setActiveStep] = useState(null);
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  
  const [applicabilityExpanded, setApplicabilityExpanded] = useState(false);
  const [stepsExpanded, setStepsExpanded] = useState(false);
  const [basicInfoExpanded, setBasicInfoExpanded] = useState(false);
  const [personnelExpanded, setPersonnelExpanded] = useState(false);
  const [schedulingExpanded, setSchedulingExpanded] = useState(false);
  const [learningExpanded, setLearningExpanded] = useState(false);
  const [evidenceBaseExpanded, setEvidenceBaseExpanded] = useState(false);
  const [editingSchedulingRule, setEditingSchedulingRule] = useState(null);
  const [editingApplicabilityRule, setEditingApplicabilityRule] = useState(null);
  const [editingAssignmentRule, setEditingAssignmentRule] = useState(null);
  
  // Evidence Base modals
  const [showGuidelineModal, setShowGuidelineModal] = useState(false);
  const [showStandardModal, setShowStandardModal] = useState(false);
  const [showRegulationModal, setShowRegulationModal] = useState(false);
  const [guidelineForm, setGuidelineForm] = useState({ name: '', source: '', url: '', year: '' });
  const [standardForm, setStandardForm] = useState({ name: '' });
  const [regulationForm, setRegulationForm] = useState({ name: '' });
  
  // Remove confirmation modals
  const [showDeleteStepModal, setShowDeleteStepModal] = useState(false);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // Clinical data state
  const [clinicalData, setClinicalData] = useState({
    organizations: [],
    locations: [],
    buildings: [],
    floors: [],
    departments: [],
    rooms: [],
    beds: [],
    equipment: [],
    personnel: [],
    loading: true
  });

  // Hooks for data
  const { 
    protocol, 
    loading: protocolLoading
  } = useProtocol(protocolId);
  
  const { 
    updateProtocol, 
    createProtocol 
  } = useProtocolMutations();
  
  const { items: categories, loading: categoriesLoading } = useProtocolCategories(true);
  const { items: protocolTypes, loading: protocolTypesLoading } = useProtocolTypes(true);
  const { items: roomTypes, loading: roomTypesLoading } = useRoomTypes(true);
  const { items: locationTypes, loading: locationTypesLoading } = useLocationTypes(true);
  const { items: frequencyTypes, loading: frequencyTypesLoading } = useFrequencyTypes(true);
  const { items: triggerEvents, loading: triggerEventsLoading } = useTriggerEvents(true);
  // Personnel assignment hooks
  const { groups, loading: groupsLoading } = useUserGroups({ isActive: true });
  const { roles } = useActiveRoles();
  const { items: assignmentTypes } = useAssignmentTypes();
  const { items: shiftTypes } = useShiftTypes();
  const { nomenclature, levels: criticalityLevels } = useRiskClassificationNomenclature(organizationId || null);
  const { rows: riskMapRows } = useRiskMapData(organizationId || null, {
    departments: clinicalData.departments,
    rooms: clinicalData.rooms,
    beds: clinicalData.beds,
    equipment: clinicalData.equipment
  });
  const [users, setUsers] = useState([]);
  


  // Load clinical data from Firebase. When organizationId is set, load only this org's structure so applicability selector shows correct departments/rooms.
  useEffect(() => {
    const loadClinicalData = async () => {
      try {
        if (organizationId) {
          const structure = await clinicalStructureService.getStructureByOrganization(organizationId);
          const organizations = await organizationService.getAll();
          setClinicalData({
            organizations: organizations || [],
            locations: structure.locations || [],
            buildings: structure.buildings || [],
            floors: structure.floors || [],
            departments: structure.departments || [],
            rooms: structure.rooms || [],
            beds: structure.beds || [],
            equipment: structure.equipment || [],
            personnel: structure.personnel || [],
            loading: false
          });
        } else {
          const [organizations, locations, buildings, floors, departments, rooms, beds, equipment, personnel] = await Promise.all([
            organizationService.getAll(),
            locationService.getAll(),
            buildingService.getAll(),
            floorService.getAll(),
            departmentService.getAll(),
            roomService.getAll(),
            bedService.getAll(),
            equipmentService.getAll(),
            personnelService.getAll()
          ]);
          setClinicalData({
            organizations: organizations || [],
            locations: locations || [],
            buildings: buildings || [],
            floors: floors || [],
            departments: departments || [],
            rooms: rooms || [],
            beds: beds || [],
            equipment: equipment || [],
            personnel: personnel || [],
            loading: false
          });
        }
      } catch (error) {
        console.error('❌ Error loading clinical data:', error);
        setClinicalData(prev => ({ ...prev, loading: false }));
      }
    };

    loadClinicalData();
  }, [organizationId]);

  // Load users for user-based assignment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await userService.getAll();
        setUsers(usersData || []);
      } catch (error) {
        console.error('Error loading users:', error);
        setUsers([]);
      }
    };
    loadUsers();
  }, []);

  // Resolve selected category (formData.category is the category code, e.g. "EC")
  const selectedCategory = categories.find(
    c => c.code === formData.category || c.id === formData.category
  );
  // Filter protocol types: match by category id (doc ref), categoryCode, or legacy category field
  const filteredProtocolTypes = protocolTypes.filter(type =>
    type.categoryId === selectedCategory?.id ||
    type.categoryCode === formData.category ||
    type.categoryId === formData.category ||
    type.category === formData.category
  );

  // Check if protocol types are available for the selected category
  const hasProtocolTypes = filteredProtocolTypes.length > 0;

  // Structure type definitions - using actual clinical structure data
  const structureTypes = [
    { value: 'organizations', label: 'Organizations', data: clinicalData.organizations },
    { value: 'locations', label: 'Locations', data: clinicalData.locations },
    { value: 'buildings', label: 'Buildings', data: clinicalData.buildings },
    { value: 'floors', label: 'Floors', data: clinicalData.floors },
    { value: 'departments', label: 'Departments', data: clinicalData.departments },
    { value: 'rooms', label: 'Rooms', data: clinicalData.rooms },
    { value: 'beds', label: 'Beds', data: clinicalData.beds },
    { value: 'equipment', label: 'Devices/Equipment', data: clinicalData.equipment },
    { value: 'roomTypes', label: 'Room Types', data: roomTypes }
  ];

  // Get filtered data based on parent selections
  const getFilteredData = (structureType, parentSelections) => {
    const typeConfig = structureTypes.find(st => st.value === structureType);
    if (!typeConfig || !typeConfig.data) return [];

    // If no parent selections, return all data
    if (!parentSelections || parentSelections.length === 0) {
      return typeConfig.data;
    }

    // Filter based on parent selections
    return typeConfig.data.filter(item => {
      // This is a simplified filter - in reality you'd check parent relationships
      // For now, return all items
      return true;
    });
  };

  // PRD 5.5: Restrict applicability entity picker to entities whose effective criticality matches protocol level.
  // When filter would return 0 (no risk map or no match), show all items so the user can still select.
  const structureTypeToEntityType = { departments: 'department', rooms: 'room', beds: 'bed', equipment: 'equipment' };
  const applyCriticalityFilter = (items, structureType, filterOn, criticalityLevelId) => {
    if (!filterOn || !criticalityLevelId || !organizationId || !Array.isArray(items) || items.length === 0) return items;
    const entityType = structureTypeToEntityType[structureType];
    if (!entityType || !Array.isArray(riskMapRows) || riskMapRows.length === 0) return items;
    const matchingIds = new Set(
      riskMapRows
        .filter(r => r.entityType === entityType && r.riskLevel?.id === criticalityLevelId)
        .map(r => r.entityId)
    );
    const filtered = items.filter(item => matchingIds.has(item.id));
    // If filter would hide everything, show full list so selector is usable (e.g. risk map not seeded yet)
    if (filtered.length === 0 && items.length > 0) return items;
    return filtered;
  };

  // Add new applicability rule
  const addApplicabilityRule = () => {
    const newRule = {
      id: Date.now().toString(),
      structureType: 'organizations',
      selectedItems: [],
      includeAll: false,
      saved: false
    };
    
    setFormData(prev => ({
      ...prev,
      applicabilityRules: [...prev.applicabilityRules, newRule]
    }));
    setEditingApplicabilityRule(newRule.id);
  };

  // Save individual applicability rule
  const saveApplicabilityRule = async (ruleId) => {
    const rule = formData.applicabilityRules.find(r => r.id === ruleId);
    if (!rule) return;

    // Validate rule
    if (!rule.structureType) {
      notify.error(t('protocols:structureTypeRequired'));
      return;
    }

    if (!rule.includeAll && getSelectedIds(rule).length === 0) {
      notify.error(t('protocols:selectAtLeastOneOrSelectAll'));
      return;
    }

    try {
      // Mark as saved
      updateApplicabilityRule(ruleId, { saved: true });
      console.log('✅ Applicability rule saved:', rule);
    } catch (error) {
      console.error('Error saving applicability rule:', error);
      notify.error(t('protocols:failedToSaveApplicabilityRule'));
    }
  };

  // Remove applicability rule
  const removeApplicabilityRule = (ruleId) => {
    setFormData(prev => ({
      ...prev,
      applicabilityRules: prev.applicabilityRules.filter(rule => rule.id !== ruleId)
    }));
  };

  // Update applicability rule
  const updateApplicabilityRule = (ruleId, updates) => {
    setFormData(prev => ({
      ...prev,
      applicabilityRules: prev.applicabilityRules.map(rule => 
        rule.id === ruleId ? { ...rule, ...updates, saved: rule.saved && !Object.keys(updates).some(k => k !== 'saved') } : rule
      )
    }));
  };

  // Handle structure type change
  const handleStructureTypeChange = (ruleId, structureType) => {
    updateApplicabilityRule(ruleId, { 
      structureType, 
      selectedItems: [], 
      includeAll: false 
    });
  };

  // Normalize selectedItems: support both [id, id, ...] and [{ id, name }, ...] (e.g. from seed/patch)
  const getSelectedIds = (rule) => {
    if (!rule?.selectedItems || !Array.isArray(rule.selectedItems)) return [];
    const first = rule.selectedItems[0];
    return typeof first === 'object' && first !== null && 'id' in first
      ? rule.selectedItems.map((s) => s.id)
      : rule.selectedItems;
  };

  // Handle item selection
  const handleItemSelection = (ruleId, itemId, isSelected) => {
    const rule = formData.applicabilityRules.find(r => r.id === ruleId);
    if (!rule) return;

    const currentIds = getSelectedIds(rule);
    const newSelectedItems = isSelected
      ? [...currentIds, itemId]
      : currentIds.filter((id) => id !== itemId);

    updateApplicabilityRule(ruleId, { selectedItems: newSelectedItems, saved: false });
  };

  // Handle "Select All" toggle
  const handleSelectAll = (ruleId, includeAll) => {
    const rule = formData.applicabilityRules.find(r => r.id === ruleId);
    if (!rule) return;

    if (includeAll) {
      updateApplicabilityRule(ruleId, { 
        includeAll: true, 
        selectedItems: [],
        saved: false
      });
    } else {
      updateApplicabilityRule(ruleId, { 
        includeAll: false,
        saved: false
      });
    }
  };

  // Add new assignment rule
  const addAssignmentRule = () => {
    const newRule = {
      id: Date.now().toString(),
          assignmentType: null,
          assignedToGroup: null,
          assignedToGroupName: null,
          requiredRoles: [],
          assignedToUser: null,
          assignedToUserName: null,
          shiftType: null,
          preferredShift: null,
          workloadThreshold: 5,
      assignmentNotes: '',
      saved: false
    };
    
    setFormData(prev => ({
      ...prev,
      assignmentRules: [...prev.assignmentRules, newRule]
    }));
    setEditingAssignmentRule(newRule.id); // Set editing state to open form
  };

  // Remove assignment rule
  const removeAssignmentRule = (ruleId) => {
    setFormData(prev => ({
      ...prev,
      assignmentRules: prev.assignmentRules.filter(rule => rule.id !== ruleId)
    }));
  };

  // Update assignment rule
  const updateAssignmentRule = (ruleId, updates) => {
    setFormData(prev => ({
      ...prev,
      assignmentRules: prev.assignmentRules.map(rule => 
        rule.id === ruleId ? { ...rule, ...updates } : rule
      )
    }));
  };

  // Save individual assignment rule
  const saveAssignmentRule = async (ruleId) => {
    const rule = formData.assignmentRules.find(r => r.id === ruleId);
    if (!rule) return;

    // Validate rule
    if (!rule.assignmentType) {
      notify.error(t('protocols:assignmentTypeRequired'));
      return;
    }

    try {
      // Mark as saved
      updateAssignmentRule(ruleId, { saved: true });
      setEditingAssignmentRule(null); // Clear editing state to close form
      console.log('✅ Assignment rule saved:', rule);
    } catch (error) {
      console.error('Error saving assignment rule:', error);
      notify.error(t('protocols:failedToSaveAssignmentRule'));
    }
  };

  // Assignment rules handlers
  const handleAssignmentChange = (ruleId, field, value) => {
    updateAssignmentRule(ruleId, { [field]: value, saved: false });
    
    // Clear dependent fields when assignment type changes
    if (field === 'assignmentType') {
      const rule = formData.assignmentRules.find(r => r.id === ruleId);
      if (rule && rule.assignmentType !== value) {
        updateAssignmentRule(ruleId, {
          assignedToGroup: null,
          assignedToGroupName: null,
          requiredRoles: [],
          assignedToUser: null,
          assignedToUserName: null,
          shiftType: null,
          saved: false
        });
      }
    }
  };

  // Add new scheduling rule (new shape: recurrenceType, times, daysOfWeek, monthly/yearly fields)
  const addSchedulingRule = () => {
    const newRule = createEmptyScheduleRule();
    setFormData(prev => ({
      ...prev,
      schedulingRules: [...prev.schedulingRules, newRule]
    }));
    setEditingSchedulingRule(newRule.id);
  };

  // Remove scheduling rule
  const removeSchedulingRule = (ruleId) => {
    setFormData(prev => ({
      ...prev,
      schedulingRules: prev.schedulingRules.filter(rule => rule.id !== ruleId)
    }));
  };

  // Update scheduling rule
  const updateSchedulingRule = (ruleId, updates) => {
    setFormData(prev => ({
      ...prev,
      schedulingRules: prev.schedulingRules.map(rule => 
        rule.id === ruleId ? { ...rule, ...updates } : rule
      )
    }));
  };

  // Save individual scheduling rule
  const saveSchedulingRule = async (ruleId) => {
    const rule = formData.schedulingRules.find(r => r.id === ruleId);
    if (!rule) return;

    const r = normalizeLegacyRule(rule);
    if (!r.frequency) {
      notify.error(t('protocols:scheduleTypeRequired'));
      return;
    }
    if (isScheduledType(r)) {
      if (!r.times || r.times.length === 0) {
        notify.error(t('protocols:atLeastOneTimeRequired'));
        return;
      }
      if (r.recurrenceType === RECURRENCE.WEEKLY && (!r.daysOfWeek || r.daysOfWeek.length === 0)) {
        notify.error(t('protocols:selectDayOfWeek'));
        return;
      }
      if (r.recurrenceType === RECURRENCE.MONTHLY && r.dayOfMonth == null && (r.weekInMonth == null || r.dayOfWeek == null)) {
        notify.error(t('protocols:monthlyRuleHint'));
        return;
      }
      if (r.recurrenceType === RECURRENCE.YEARLY && (r.month == null || r.yearDayOfMonth == null)) {
        notify.error(t('protocols:yearlyRuleHint'));
        return;
      }
    }

    try {
      updateSchedulingRule(ruleId, { saved: true });
      console.log('✅ Scheduling rule saved:', r);
    } catch (error) {
      console.error('Error saving scheduling rule:', error);
      notify.error(t('protocols:failedToSaveSchedulingRule'));
    }
  };

  // Handle scheduling rule changes
  const handleSchedulingRuleChange = (ruleId, field, value) => {
    updateSchedulingRule(ruleId, { [field]: value, saved: false });
  };

  // Handle scheduling time constraint changes
  const handleSchedulingTimeConstraintChange = (ruleId, field, value) => {
    const rule = formData.schedulingRules.find(r => r.id === ruleId);
    if (!rule) return;
    
    updateSchedulingRule(ruleId, {
      timeConstraints: {
        ...rule.timeConstraints,
        [field]: value
      },
      saved: false
    });
  };

  // Handle scheduling trigger events
  const handleSchedulingTriggerEvents = (ruleId, eventId, checked) => {
    const rule = formData.schedulingRules.find(r => r.id === ruleId);
    if (!rule) return;
    const newValue = checked ? [...(rule.triggerEvents || []), eventId] : (rule.triggerEvents || []).filter(id => id !== eventId);
    updateSchedulingRule(ruleId, { triggerEvents: newValue, saved: false });
  };

  // Time slots: add one
  const addSchedulingTimeSlot = (ruleId) => {
    const rule = formData.schedulingRules.find(r => r.id === ruleId);
    if (!rule) return;
    const times = (rule.times && rule.times.length) ? [...rule.times] : [{ hour: 8, minute: 0 }];
    const last = times[times.length - 1];
    times.push({ hour: (last && last.hour != null) ? last.hour : 8, minute: (last && last.minute != null) ? last.minute : 0 });
    updateSchedulingRule(ruleId, { times, saved: false });
  };

  // Time slots: remove one
  const removeSchedulingTimeSlot = (ruleId, index) => {
    const rule = formData.schedulingRules.find(r => r.id === ruleId);
    if (!rule) return;
    const times = (rule.times && rule.times.length) ? [...rule.times] : [{ hour: 8, minute: 0 }];
    if (times.length <= 1) return;
    times.splice(index, 1);
    updateSchedulingRule(ruleId, { times, saved: false });
  };

  // Time slot change (single slot: hour or minute)
  const handleSchedulingTimeSlotChange = (ruleId, index, field, value) => {
    const rule = formData.schedulingRules.find(r => r.id === ruleId);
    if (!rule) return;
    const times = (rule.times && rule.times.length) ? [...rule.times] : [{ hour: 8, minute: 0 }];
    if (!times[index]) times[index] = { hour: 8, minute: 0 };
    times[index] = { ...times[index], [field]: value };
    updateSchedulingRule(ruleId, { times, saved: false });
  };

  // Time slot change from native time input (HH:mm) — sets both hour and minute
  const handleSchedulingTimeSlotTimeInput = (ruleId, index, timeStr) => {
    const parsed = parseTimeSlot(timeStr);
    if (!parsed) return;
    const rule = formData.schedulingRules.find(r => r.id === ruleId);
    if (!rule) return;
    const times = (rule.times && rule.times.length) ? [...rule.times] : [{ hour: 8, minute: 0 }];
    if (!times[index]) times[index] = { hour: 8, minute: 0 };
    times[index] = { hour: parsed.hour, minute: parsed.minute };
    updateSchedulingRule(ruleId, { times, saved: false });
  };

  // Weekly: toggle day of week
  const handleSchedulingDayOfWeek = (ruleId, dayValue, checked) => {
    const rule = formData.schedulingRules.find(r => r.id === ruleId);
    if (!rule) return;
    const days = [...(rule.daysOfWeek || [])];
    const n = Number(dayValue);
    if (checked) { if (!days.includes(n)) days.push(n); }
    else { const i = days.indexOf(n); if (i !== -1) days.splice(i, 1); }
    days.sort((a, b) => a - b);
    updateSchedulingRule(ruleId, { daysOfWeek: days, saved: false });
  };

  // Load protocol data if editing
  useEffect(() => {
    if (protocolId && protocol) {
      setFormData({
        ...protocol,
        protocol_code: protocol.protocol_code || '',
        is_template: protocol.is_template === true || protocol.status === 'Template' || protocol.status === 'template',
        evidence_base: protocol.evidence_base || {
          guidelines: [],
          standards: [],
          regulations: []
        },
        assignmentRules: Array.isArray(protocol.assignmentRules) ? protocol.assignmentRules : (protocol.assignmentRules && typeof protocol.assignmentRules === 'object' ? [{ ...protocol.assignmentRules, id: Date.now().toString(), saved: true }] : []),
        schedulingRules: Array.isArray(protocol.schedulingRules)
          ? protocol.schedulingRules.map(normalizeLegacyRule)
          : (protocol.frequency || protocol.timeConstraints || (protocol.triggerEvents && protocol.triggerEvents.length > 0)
            ? [normalizeLegacyRule({
                id: Date.now().toString(),
                frequency: protocol.frequency || '',
                frequencyValue: protocol.frequencyValue || 1,
                triggerEvents: protocol.triggerEvents || [],
                timeConstraints: protocol.timeConstraints || { maxDuration: 30, timeoutAction: 'escalate', escalationContact: '' },
                saved: true
              })]
            : []),
        applicabilityRules: protocol.applicabilityRules || [],
        applicablePersonnel: protocol.applicablePersonnel || [],
        clinicalContexts: protocol.clinicalContexts || [],
        conditions: [], // No longer used at protocol level (condition types are for supplies etc.)
        steps: protocol.steps || [],
        generateTaskAtProtocolLevel: protocol.generateTaskAtProtocolLevel === true,
        structuralCriticalityLevelId: protocol.structuralCriticalityLevelId ?? null,
        structuralCriticalityLevelLabel: protocol.structuralCriticalityLevelLabel ?? null,
        filterByProtocolRiskLevel: protocol.filterByProtocolRiskLevel !== false,
        learningAssessment: protocol.learningAssessment && typeof protocol.learningAssessment === 'object'
          ? {
              questionCount: Math.min(20, Math.max(5, Number(protocol.learningAssessment.questionCount) || 10)),
              passThresholdPercent: Math.min(100, Math.max(50, Number(protocol.learningAssessment.passThresholdPercent) || 70)),
              cooldownMinutes: Math.min(1440, Math.max(5, Number(protocol.learningAssessment.cooldownMinutes) || 60)),
              generateQuiz: protocol.learningAssessment.generateQuiz === true
            }
          : { questionCount: 10, passThresholdPercent: 70, cooldownMinutes: 60, generateQuiz: false }
      });
    }
  }, [protocolId, protocol]);

  // Enhanced validation with comprehensive checks
  const validateForm = () => {
    const newErrors = {};
    
    // Basic information validation
    if (!formData.name.trim()) {
      newErrors.name = t('protocols:protocolNameRequired');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('protocols:protocolNameMinLength');
    } else if (formData.name.trim().length > 200) {
      newErrors.name = t('protocols:protocolNameMaxLength');
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t('protocols:descriptionRequired');
    } else if (formData.description.trim().length < 10) {
      newErrors.description = t('protocols:descriptionMinLength');
    } else if (formData.description.trim().length > 5000) {
      newErrors.description = t('protocols:descriptionMaxLength');
    }
    
    if (!formData.category) {
      newErrors.category = t('protocols:protocolCategoryRequired');
    }
    
    // Protocol code validation (required only; format is free so you can codify as you want)
    if (!formData.protocol_code.trim()) {
      newErrors.protocol_code = t('protocols:protocolCodeRequired');
    } else if (formData.protocol_code.trim().length > 64) {
      newErrors.protocol_code = t('protocols:protocolCodeMaxLength');
    }
    
    // Version validation
    if (formData.version && formData.version.trim()) {
      const versionRegex = /^[0-9]+(\.[0-9]+)*$/;
      if (!versionRegex.test(formData.version.trim())) {
        newErrors.version = t('protocols:versionInvalid');
      }
    }
    
    // Applicability rules validation
    if (formData.applicabilityRules.length === 0) {
      newErrors.applicability = t('protocols:atLeastOneApplicabilityRule');
    } else {
      formData.applicabilityRules.forEach((rule, index) => {
        if (!rule.structureType) {
          newErrors[`applicability_${index}_type`] = t('protocols:ruleNStructureTypeRequired', { number: index + 1 });
        }
        if (!rule.includeAll && getSelectedIds(rule).length === 0) {
          newErrors[`applicability_${index}_items`] = t('protocols:ruleNSelectAtLeastOneItem', { number: index + 1 });
        }
      });
    }
    
    // Steps validation
    if (formData.steps.length === 0) {
      newErrors.steps = t('protocols:atLeastOneStepRequired');
    } else {
      const stepOrders = new Set();
      const stepNames = new Set();
      
      formData.steps.forEach((step, index) => {
        // Step name validation
        const stepNum = index + 1;
        if (!step.name.trim()) {
          newErrors[`step_${index}_name`] = t('protocols:stepNNameRequired', { step: stepNum });
        } else if (step.name.trim().length < 3) {
          newErrors[`step_${index}_name`] = t('protocols:stepNNameMinLength', { step: stepNum });
        } else if (stepNames.has(step.name.trim().toLowerCase())) {
          newErrors[`step_${index}_name`] = t('protocols:stepNNameUnique', { step: stepNum });
        } else {
          stepNames.add(step.name.trim().toLowerCase());
        }
        
        // Step description validation
        if (!step.description.trim()) {
          newErrors[`step_${index}_description`] = t('protocols:stepDescriptionRequired', { step: stepNum });
        } else if (step.description.trim().length < 10) {
          newErrors[`step_${index}_description`] = t('protocols:stepDescriptionMinLength', { step: stepNum });
        }
        
        // Step duration validation
        if (!step.estimatedDuration || step.estimatedDuration <= 0) {
          newErrors[`step_${index}_duration`] = t('protocols:stepNDurationRequired', { step: stepNum });
        } else if (step.estimatedDuration > 1440) { // 24 hours
          newErrors[`step_${index}_duration`] = t('protocols:stepNDurationMax', { step: stepNum });
        }
        
        // Step order validation
        if (!step.order || step.order < 1) {
          newErrors[`step_${index}_order`] = t('protocols:stepNOrderRequired', { step: stepNum });
        } else if (stepOrders.has(step.order)) {
          newErrors[`step_${index}_order`] = t('protocols:stepNOrderUnique', { step: stepNum });
        } else {
          stepOrders.add(step.order);
        }
        
        // Actions validation for each step
        if (step.actions && step.actions.length > 0) {
          const actionOrders = new Set();
          const actionNames = new Set();
          
          step.actions.forEach((action, actionIndex) => {
            const actionNum = actionIndex + 1;
            if (!action.name.trim()) {
              newErrors[`step_${index}_action_${actionIndex}_name`] = t('protocols:actionNNameRequired', { step: stepNum, action: actionNum });
            } else if (action.name.trim().length < 3) {
              newErrors[`step_${index}_action_${actionIndex}_name`] = t('protocols:actionNNameMinLength', { step: stepNum, action: actionNum });
            } else if (actionNames.has(action.name.trim().toLowerCase())) {
              newErrors[`step_${index}_action_${actionIndex}_name`] = t('protocols:actionNNameUnique', { step: stepNum, action: actionNum });
            } else {
              actionNames.add(action.name.trim().toLowerCase());
            }
            
            if (!action.description.trim()) {
              newErrors[`step_${index}_action_${actionIndex}_description`] = t('protocols:actionDescriptionRequired', { step: stepNum, action: actionNum });
            }
            
            if (action.order && actionOrders.has(action.order)) {
              newErrors[`step_${index}_action_${actionIndex}_order`] = t('protocols:actionNOrderUnique', { step: stepNum, action: actionNum });
            } else if (action.order) {
              actionOrders.add(action.order);
            }
          });
        }
      });
    }
    
    // Learning & Assessment (quiz PRD): questionCount 5-20, passThreshold 50-100, cooldown 5-1440
    const la = formData.learningAssessment || {};
    const qCount = Number(la.questionCount);
    const passThresh = Number(la.passThresholdPercent);
    const cooldown = Number(la.cooldownMinutes);
    if (qCount < 5 || qCount > 20 || !Number.isInteger(qCount)) {
      newErrors.learningQuestionCount = t('protocols:learningQuestionCountRange');
    }
    if (passThresh < 50 || passThresh > 100 || !Number.isInteger(passThresh)) {
      newErrors.learningPassThreshold = t('protocols:learningPassThresholdRange');
    }
    if (cooldown < 5 || cooldown > 1440 || !Number.isInteger(cooldown)) {
      newErrors.learningCooldown = t('protocols:learningCooldownRange');
    }
    
    const isValid = Object.keys(newErrors).length === 0;
    if (!isValid) {
      newErrors.validationSummary = t('protocols:validationSummaryFull');
    }
    setErrors(newErrors);
    return isValid;
  };

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error and validation summary when user starts typing
    if (errors[field] || errors.validationSummary) {
      setErrors(prev => {
        const next = { ...prev, [field]: null };
        delete next.validationSummary;
        return next;
      });
    }
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  // Step management
  const handleAddStep = () => {
    const newStep = {
      id: Date.now().toString(),
      stepNumber: formData.steps.length + 1,
      order: formData.steps.length + 1,
      name: '',
      description: '',
      estimatedDuration: 5,
      isRequired: true,
      actions: [],
      prerequisites: [],
      verificationMethods: []
    };
    
    // Don't add to formData yet - only when user clicks Save
    setActiveStep(newStep);
    setShowStepEditor(true);
    
    // Try multiple scroll methods
    setTimeout(() => {
      // Method 1: Standard window scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Method 2: Try to find and scroll the step editor header
      const stepEditorHeader = document.querySelector('.step-editor-header');
      if (stepEditorHeader) {
        stepEditorHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      
      // Method 3: Try to find the step editor container
      const stepEditor = document.querySelector('.step-editor');
      if (stepEditor) {
        stepEditor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    
    // Additional attempts
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 600);
  };

  const handleEditStep = (step) => {
    setActiveStep(step);
    setShowStepEditor(true);
    
    // Try multiple scroll methods
    setTimeout(() => {
      // Method 1: Standard window scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Method 2: Try to find and scroll the step editor header
      const stepEditorHeader = document.querySelector('.step-editor-header');
      if (stepEditorHeader) {
        stepEditorHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      
      // Method 3: Try to find the step editor container
      const stepEditor = document.querySelector('.step-editor');
      if (stepEditor) {
        stepEditor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    
    // Additional attempts
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 600);
  };

  const handleDeleteStep = (stepId) => {
    const step = formData.steps.find(s => s.id === stepId);
    setStepToDelete({ id: stepId, name: step?.name || `Step ${step?.stepNumber || ''}` });
    setShowDeleteStepModal(true);
  };

  const confirmDeleteStep = () => {
    if (stepToDelete) {
      setFormData(prev => ({
        ...prev,
        steps: prev.steps
          .filter(step => step.id !== stepToDelete.id)
          .map((step, index) => ({
            ...step,
            stepNumber: index + 1
          }))
      }));
      setShowDeleteStepModal(false);
      setStepToDelete(null);
      console.log('✅ Step deleted successfully');
    }
  };

  const handleStepSave = (updatedStep) => {
    setFormData(prev => {
      const existingStepIndex = prev.steps.findIndex(step => step.id === updatedStep.id);
      
      if (existingStepIndex >= 0) {
        // Update existing step
        const updatedSteps = [...prev.steps];
        updatedSteps[existingStepIndex] = updatedStep;
        return {
          ...prev,
          steps: updatedSteps
        };
      } else {
        // Add new step
        return {
          ...prev,
          steps: [...prev.steps, updatedStep]
        };
      }
    });
    
    setShowStepEditor(false);
    setActiveStep(null);
    console.log('✅ Step saved successfully');
  };

  // Drag and drop handlers for steps
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before activating drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [activeId, setActiveId] = useState(null);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setFormData(prev => {
        const oldIndex = prev.steps.findIndex(step => step.id === active.id);
        const newIndex = prev.steps.findIndex(step => step.id === over.id);
        
        const newSteps = arrayMove(prev.steps, oldIndex, newIndex);
        
        // Update order numbers
        const reorderedSteps = newSteps.map((step, index) => ({
          ...step,
          order: index + 1,
          stepNumber: index + 1
        }));
        
        return {
          ...prev,
          steps: reorderedSteps
        };
      });
    }
  };

  // Save protocol with enhanced error handling
  const handleSave = async () => {
    if (!validateForm()) {
      // Scroll to top to show validation errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsSaving(true);
    setErrors({}); // Clear previous errors
    
    try {
      const protocolData = {
        ...formData,
        ...(organizationId != null && { organizationId }),
        lastModified: new Date().toISOString(),
        modifiedBy: 'current-user', // TODO: Get from auth context
        createdAt: protocolId ? formData.createdAt : new Date().toISOString(),
        createdBy: protocolId ? formData.createdBy : 'current-user'
      };
      
      if (protocolId) {
        await updateProtocol(protocolId, protocolData, 'current-user'); // TODO: Get actual userId from auth
        console.log('✅ Protocol updated successfully');
      } else {
        await createProtocol(protocolData, 'current-user'); // TODO: Get actual userId from auth
        console.log('✅ Protocol created successfully');
      }
      
      onSave && onSave(protocolData);
      onClose && onClose();
    } catch (error) {
      console.error('❌ Error saving protocol:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to save protocol';
      if (error.message) {
        if (error.message.includes('permission')) {
          errorMessage = 'Permission denied. You may not have access to save protocols.';
        } else if (error.message.includes('network') || error.message.includes('timeout')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('quota')) {
          errorMessage = 'Storage quota exceeded. Please contact your administrator.';
        } else {
          errorMessage = `Save failed: ${error.message}`;
        }
      }
      
      setErrors({ save: errorMessage });
      
      // Scroll to error message
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } finally {
      setIsSaving(false);
    }
  };

  if (protocolLoading || categoriesLoading || roomTypesLoading || frequencyTypesLoading || triggerEventsLoading || clinicalData.loading) {
    return <div className="loading-container">Loading protocol data...</div>;
  }

  if (showStepEditor && activeStep) {
    return (
      <ProtocolStepEditor
        step={activeStep}
        onSave={handleStepSave}
        onCancel={() => {
          setShowStepEditor(false);
          setActiveStep(null);
        }}
        roomTypes={roomTypes}
        triggerEvents={triggerEvents}
        protocolId={protocolId}
        protocolName={formData.name}
      />
    );
  }

  return (
    <div className="protocol-form">
      <div className="protocol-form-header">
        <h2>{protocolId ? t('protocols:editProtocol') : t('protocols:createNewProtocol')}</h2>
        <div className="protocol-form-actions">
          <button 
            type="button" 
            onClick={onCancel || onClose}
            className="btn btn-secondary"
          >
            <X size={16} />
            {t('protocols:cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="btn btn-primary"
            title={isSaving ? t('protocols:saving') : t('protocols:saveProtocol')}
          >
            <Save size={16} />
            {isSaving ? t('protocols:saving') : t('protocols:saveProtocol')}
          </button>
        </div>
      </div>

      {errors.save && (
        <div className="error-message">
          {errors.save}
        </div>
      )}
      {errors.validationSummary && (
        <div className="error-message protocol-form-validation-summary" role="alert">
          {errors.validationSummary}
        </div>
      )}

      <div className="protocol-form-content">
        {/* Basic Information */}
        <div className="form-section">
          <div className="section-header-non-collapsible">
            <h3>
              {t('protocols:basicInformation')}
              {[formData.name, formData.category, formData.protocol_code, formData.description].filter(f => f && f.trim()).length > 0 && (
                <span className="risk-count-badge">
                  {[formData.name, formData.category, formData.protocol_code, formData.description].filter(f => f && f.trim()).length}
                </span>
              )}
            </h3>
          </div>

          <>
          <div className="form-group">
            <label htmlFor="name">{t('protocols:protocolName')}</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={errors.name ? 'error' : ''}
              placeholder={t('protocols:protocolNamePlaceholder')}
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="protocol-basic-grid">
            <div className="form-group">
              <label htmlFor="category">{t('protocols:categoryRequired')}</label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => {
                  handleInputChange('category', e.target.value);
                  // Clear protocol type when category changes
                  handleInputChange('protocolType', '');
                }}
                className={errors.category ? 'error' : ''}
              >
                <option value="">{t('protocols:selectCategory')}</option>
                {categories && categories.map(cat => (
                  <option key={cat.id} value={cat.code}>
                    {cat.name} ({cat.code})
                  </option>
                ))}
              </select>
              {errors.category && <span className="error-text">{errors.category}</span>}
            </div>

            {hasProtocolTypes && (
              <div className="form-group">
                <label htmlFor="protocolType">{t('protocols:protocolTypeOptional')}</label>
                <select
                  id="protocolType"
                  value={formData.protocolType}
                  onChange={(e) => handleInputChange('protocolType', e.target.value)}
                  disabled={!formData.category || protocolTypesLoading}
                >
                  <option value="">
                    {protocolTypesLoading ? t('protocols:loadingProtocolTypes') : t('protocols:selectProtocolTypeOptional')}
                  </option>
                  {filteredProtocolTypes && filteredProtocolTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {!formData.category && !protocolTypesLoading && (
                  <small className="form-hint">Select a category first to see available protocol types</small>
                )}
                {protocolTypesLoading && (
                  <small className="form-hint">Loading protocol types...</small>
                )}
                {formData.category && !protocolTypesLoading && !hasProtocolTypes && (
                  <small className="form-hint">No protocol types available for this category. You can still create the protocol without a specific type.</small>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="status">{t('protocols:status')}</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="Deprecated">Deprecated</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="structuralCriticalityLevelId">{t('protocols:structuralCriticalityLevel')}</label>
              <select
                id="structuralCriticalityLevelId"
                value={formData.structuralCriticalityLevelId || ''}
                onChange={(e) => {
                  const id = e.target.value || null;
                  const level = (criticalityLevels || []).find((l) => l.id === id);
                  setFormData((prev) => ({
                    ...prev,
                    structuralCriticalityLevelId: id,
                    structuralCriticalityLevelLabel: level ? level.label : null
                  }));
                }}
              >
                <option value="">{organizationId ? (t('protocols:noCriticalityLevel') || '— Not set') : (t('protocols:selectOrgForCriticality') || 'Select organization')}</option>
                {(criticalityLevels || []).sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999)).map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.label}
                  </option>
                ))}
              </select>
              {organizationId && !(criticalityLevels && criticalityLevels.length > 0) && (
                <small className="form-hint">{t('protocols:configureRiskNomenclature')}</small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="is_template" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  id="is_template"
                  checked={formData.is_template}
                  onChange={(e) => handleInputChange('is_template', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>{t('protocols:markAsTemplate')}</span>
              </label>
              <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                {t('protocols:templateHint')}
              </small>
            </div>

            <div className="form-group">
            <label htmlFor="protocol_code">{t('protocols:protocolCode')}</label>
            <input
              type="text"
              id="protocol_code"
              value={formData.protocol_code}
              onChange={(e) => handleInputChange('protocol_code', e.target.value)}
              className={errors.protocol_code ? 'error' : ''}
              placeholder={t('protocols:protocolCodePlaceholder')}
              maxLength={64}
              title={t('protocols:protocolCodeHint')}
            />
            {errors.protocol_code && <span className="error-text">{errors.protocol_code}</span>}
            <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
              {t('protocols:protocolCodeHint')}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="version">{t('protocols:version')}</label>
              <input
                type="text"
                id="version"
                value={formData.version}
                onChange={(e) => handleInputChange('version', e.target.value)}
              placeholder={t('protocols:versionPlaceholder')}
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="description">{t('protocols:description')}</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          className={errors.description ? 'error' : ''}
          rows={3}
          placeholder={t('protocols:descriptionPlaceholder')}
        />
            {errors.description && <span className="error-text">{errors.description}</span>}
          </div>

          <div className="form-group full-width">
            <label>
              <input
                type="checkbox"
                checked={formData.generateTaskAtProtocolLevel === true}
                onChange={(e) => handleInputChange('generateTaskAtProtocolLevel', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              {t('protocols:generateTaskAtProtocolLevel')}
            </label>
            <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
              {t('protocols:generateTaskHint')}
            </small>
          </div>
          </>
        </div>

        {/* Applicability */}
        <div className="form-section">
          <div 
            className="section-header-collapsible"
            onClick={() => setApplicabilityExpanded(!applicabilityExpanded)}
          >
            <h3>
              {t('protocols:applicabilityRules')}
              {formData.applicabilityRules && formData.applicabilityRules.length > 0 && (
                <span className="risk-count-badge">
                  {formData.applicabilityRules.length}
                </span>
              )}
            </h3>
            <div className="section-header-actions">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  addApplicabilityRule();
                }}
                className="btn btn-success section-add-btn"
              >
                <Plus size={16} />
                {t('protocols:addApplicabilityRule')}
              </button>
              {applicabilityExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
          
          {applicabilityExpanded && (
            <>
          {errors.applicability && (
            <div className="error-message">
              {errors.applicability}
            </div>
          )}

          {/* PRD 5.5: Filter by protocol risk level — defaults to on when protocol has criticality and org is set */}
          {organizationId && formData.structuralCriticalityLevelId && (
            <div className="applicability-filter-by-risk">
              <label className="checkbox-label-inline">
                <input
                  type="checkbox"
                  checked={formData.filterByProtocolRiskLevel !== false}
                  onChange={(e) => setFormData(prev => ({ ...prev, filterByProtocolRiskLevel: e.target.checked }))}
                />
                <span>{t('protocols:filterByProtocolRiskLevel')}</span>
              </label>
              <p className="section-hint">{t('protocols:filterByProtocolRiskLevelHint')}</p>
            </div>
          )}

          <div className="applicability-rules">
            {/* Saved rules - compact view */}
            {formData.applicabilityRules
              .filter(rule => rule.saved && editingApplicabilityRule !== rule.id)
              .map((rule, index) => {
              const typeConfig = structureTypes.find(st => st.value === rule.structureType);
                const savedIndex = formData.applicabilityRules.filter(r => r.saved).findIndex(r => r.id === rule.id);
              return (
                  <div key={rule.id} className="applicability-rule-item">
                    <div className="rule-info">
                      <span className="rule-number">Rule {savedIndex + 1}</span>
                      <span className="rule-summary">
                        {typeConfig?.label || 'Unknown'} - {rule.includeAll ? 'All items' : `${getSelectedIds(rule).length} items selected`}
                      </span>
                    </div>
                    <div className="rule-actions">
                      <button
                        type="button"
                        onClick={() => setEditingApplicabilityRule(rule.id)}
                        className="btn btn-edit"
                        title="Edit this rule"
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                    <button
                      type="button"
                      onClick={() => removeApplicabilityRule(rule.id)}
                        className="btn btn-delete"
                      title="Remove this rule"
                    >
                      <Trash2 size={16} />
                        Remove
                    </button>
                  </div>
                  </div>
                );
              })}
            
            {/* Editing/Adding rule - full form */}
            {formData.applicabilityRules
              .filter(rule => !rule.saved || editingApplicabilityRule === rule.id)
              .map((rule, index) => {
                const typeConfig = structureTypes.find(st => st.value === rule.structureType);
                let availableItems = getFilteredData(rule.structureType, []);
                availableItems = applyCriticalityFilter(availableItems, rule.structureType, formData.filterByProtocolRiskLevel, formData.structuralCriticalityLevelId);
                const savedIndex = rule.saved ? formData.applicabilityRules.filter(r => r.saved).findIndex(r => r.id === rule.id) : -1;
                
                return (
                  <div key={rule.id} className="applicability-rule-form">
                    <div className="rule-header">
                      <span className="rule-number">{rule.saved ? t('protocols:editRuleNumber', { number: savedIndex + 1 }) : t('protocols:newRule')}</span>
                  </div>
                  
                  <div className="rule-content">
                    <div className="rule-left">
                      <label>Structure Type</label>
                      <select
                        value={rule.structureType}
                        onChange={(e) => handleStructureTypeChange(rule.id, e.target.value)}
                        className="structure-type-select"
                      >
                        <option value="">Select Structure Type</option>
                        {structureTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="rule-right">
                      <div className="rule-right-header">
                        <label>Select {typeConfig?.label || 'Items'}</label>
                        <div className="select-all-controls">
                          <label className="select-all-label">
                            <input
                              type="checkbox"
                              checked={rule.includeAll}
                              onChange={(e) => handleSelectAll(rule.id, e.target.checked)}
                            />
                            Select All
                          </label>
                        </div>
                      </div>
                      
                      <div className="rule-items">
                        {availableItems.length === 0 ? (
                          <div className="no-items-message">
                            No {typeConfig?.label.toLowerCase() || 'items'} available. Please ensure data is loaded.
                          </div>
                        ) : (
                          availableItems.map(item => (
                            <label key={item.id} className="rule-item">
                              <input
                                type="checkbox"
                                checked={rule.includeAll || getSelectedIds(rule).includes(item.id)}
                                disabled={rule.includeAll}
                                onChange={(e) => handleItemSelection(rule.id, item.id, e.target.checked)}
                              />
                              <span className="item-name">
                                {item.name}
                                {item.code && ` (${item.code})`}
                                {item.floorNumber && ` - Floor ${item.floorNumber}`}
                                {item.roomType && ` - ${item.roomType}`}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                      
                      <div className="rule-summary">
                        {rule.includeAll ? (
                          <span className="summary-text">All {typeConfig?.label.toLowerCase() || 'items'} selected</span>
                        ) : (
                          <span className="summary-text">
                            {getSelectedIds(rule).length} of {availableItems.length} {typeConfig?.label.toLowerCase() || 'items'} selected
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="rule-form-actions">
                      <button
                        type="button"
                        onClick={() => {
                          saveApplicabilityRule(rule.id);
                          setEditingApplicabilityRule(null);
                        }}
                        className="btn btn-primary"
                        title="Save this rule"
                      >
                        <Save size={16} />
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (rule.saved) {
                            setEditingApplicabilityRule(null);
                          } else {
                            removeApplicabilityRule(rule.id);
                          }
                        }}
                        className="btn btn-secondary"
                        title={rule.saved ? t('protocols:cancelEditing') : t('protocols:cancel')}
                      >
                        <X size={16} />
                        {t('protocols:cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
            </>
          )}

          {/* Personnel Assignment */}
          <div className="form-section personnel-section">
            <div 
              className="section-header-collapsible"
              onClick={() => setPersonnelExpanded(!personnelExpanded)}
            >
              <h3>
                {t('protocols:sectionPersonnelAssignment')}
                {formData.assignmentRules && formData.assignmentRules.length > 0 && (
                  <span className="risk-count-badge">
                    {formData.assignmentRules.length}
                  </span>
                )}
              </h3>
              <div className="section-header-actions">
          <button
            type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    addAssignmentRule();
                  }}
                  className="btn btn-success section-add-btn"
          >
            <Plus size={16} />
                  {t('protocols:addAssignmentRule')}
          </button>
                {personnelExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>

            {personnelExpanded && (
            <>
            {formData.assignmentRules.length === 0 && !editingAssignmentRule ? (
              <p className="no-items-message">
                No assignment rules yet. Click "{t('protocols:addAssignmentRule')}" to create one.
              </p>
            ) : (
              <div className="assignment-rules">
                {/* Saved rules - compact view */}
                {formData.assignmentRules
                  .filter(rule => rule.saved && editingAssignmentRule !== rule.id)
                  .map((rule, index) => {
                    const selectedType = assignmentTypes?.find(t => t.id === rule.assignmentType);
                    const typeName = selectedType?.name || 'No type';
                    const savedIndex = formData.assignmentRules.filter(r => r.saved).findIndex(r => r.id === rule.id);
                    let summary = `${typeName}`;
                    if (rule.assignedToGroupName) summary += ` - ${rule.assignedToGroupName}`;
                    if (rule.assignedToUserName) summary += ` - ${rule.assignedToUserName}`;
                    if (rule.shiftType) summary += ` - Shift: ${rule.shiftType}`;
                    if (rule.preferredShift) summary += ` - Pref: ${rule.preferredShift}`;
                    if (rule.workloadThreshold) summary += ` - Max: ${rule.workloadThreshold}`;
                    
                    return (
                      <div key={rule.id} className="assignment-rule-item">
                        <div className="rule-info">
                          <span className="rule-number">Assignment {savedIndex + 1}</span>
                          <span className="rule-summary">{summary}</span>
                        </div>
                        <div className="rule-actions">
                          <button
                            type="button"
                            onClick={() => setEditingAssignmentRule(rule.id)}
                            className="btn btn-edit"
                            title="Edit this rule"
                          >
                            <Edit size={16} />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => removeAssignmentRule(rule.id)}
                            className="btn btn-delete"
                            title="Remove this rule"
                          >
                            <Trash2 size={16} />
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                
                {/* Editing/Adding rule - full form */}
                {formData.assignmentRules
                  .filter(rule => !rule.saved || editingAssignmentRule === rule.id)
                  .map((rule, index) => {
                    const selectedType = assignmentTypes?.find(t => t.id === rule.assignmentType);
                    const typeName = selectedType?.name?.toLowerCase() || '';
                    
                    return (
                      <div key={rule.id} className="assignment-rule-form">
                        <div className="rule-header">
                          <span className="rule-number">{rule.saved ? `Edit Assignment ${formData.assignmentRules.filter(r => r.saved).findIndex(r => r.id === rule.id) + 1}` : `New Assignment`}</span>
                        </div>
                        
                        <div className="rule-content assignment-compact">
                          <div className="assignment-grid">
              <div className="form-group">
                <label>Assignment Type *</label>
                <select
                              value={rule.assignmentType || ''}
                  onChange={(e) => {
                    const assignmentType = e.target.value || null;
                                handleAssignmentChange(rule.id, 'assignmentType', assignmentType);
                  }}
                >
                  <option value="">Select Assignment Type</option>
                  {assignmentTypes && assignmentTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              {/* Context-Specific Assignment Fields */}
              {(() => {
                            const selectedType = assignmentTypes?.find(t => t.id === rule.assignmentType);
                const typeName = selectedType?.name?.toLowerCase() || '';
                
                // Group-Based Assignment
                if (typeName.includes('group')) {
                  return (
                    <div className="form-group" key="group-assignment">
                      <label>Assigned to Group *</label>
                      <select
                                    value={rule.assignedToGroup || ''}
                        onChange={(e) => {
                          const groupId = e.target.value || null;
                          const group = groups.find(g => g.id === groupId);
                                      handleAssignmentChange(rule.id, 'assignedToGroup', groupId);
                                      handleAssignmentChange(rule.id, 'assignedToGroupName', group?.name || null);
                        }}
                        required
                      >
                        <option value="">Select Group</option>
                        {groupsLoading ? (
                          <option disabled>Loading groups...</option>
                        ) : groups && groups.length > 0 ? (
                          groups.map(group => (
                            <option key={group.id} value={group.id}>{group.name || group.id}</option>
                          ))
                        ) : (
                          <option disabled>No groups available. Create groups in User Group Management first.</option>
                        )}
                      </select>
                    </div>
                  );
                }
                
                // Role-Based Assignment
                if (typeName.includes('role')) {
                  return (
                                <div className="form-group form-group-full" key="role-assignment">
                      <label>Required Roles *</label>
                      <select
                        multiple
                                    value={rule.requiredRoles || []}
                        onChange={(e) => {
                          const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                                      handleAssignmentChange(rule.id, 'requiredRoles', selectedOptions);
                        }}
                        size={Math.min(roles?.length || 3, 6)}
                        style={{ height: 'auto', minHeight: '100px' }}
                        required
                      >
                        {roles && roles.length > 0 ? (
                          roles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name || role.role_id}
                            </option>
                          ))
                        ) : (
                          <option disabled>No roles available</option>
                        )}
                      </select>
                    </div>
                  );
                }
                
                // Shift-Based Assignment
                if (typeName.includes('shift')) {
                  return (
                    <div className="form-group" key="shift-assignment">
                      <label>Shift Type *</label>
                      <select
                                    value={rule.shiftType || ''}
                                    onChange={(e) => handleAssignmentChange(rule.id, 'shiftType', e.target.value || null)}
                        required
                      >
                        <option value="">Select Shift</option>
                        {shiftTypes && shiftTypes.map(shift => (
                          <option key={shift.id} value={shift.id}>{shift.name}</option>
                        ))}
                      </select>
                    </div>
                  );
                }
                
                // User-Based/Direct Assignment
                if (typeName.includes('user') || typeName.includes('direct')) {
                  return (
                    <div className="form-group" key="user-direct-assignment">
                                  <label>Assign to User {typeName.includes('direct') ? '(Optional)' : '*'}</label>
                      <select
                                    value={rule.assignedToUser || ''}
                        onChange={(e) => {
                          const userId = e.target.value || null;
                          const user = users.find(u => u.id === userId);
                                      handleAssignmentChange(rule.id, 'assignedToUser', userId);
                                      handleAssignmentChange(rule.id, 'assignedToUserName', user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : null);
                        }}
                        required={!typeName.includes('direct')}
                      >
                        <option value="">Select User</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name} (${user.email || user.id})`
                              : user.email || user.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }
                
                // Department Pool Assignment
                if (typeName.includes('department')) {
                  return (
                                <div className="form-group form-group-full" key="dept-assignment">
                      <label>Assignment Notes</label>
                      <textarea
                                    value={rule.assignmentNotes || ''}
                                    onChange={(e) => handleAssignmentChange(rule.id, 'assignmentNotes', e.target.value)}
                        rows={2}
                        placeholder="Task will be assigned to first available person in department..."
                      />
                    </div>
                  );
                }
                
                // Skill-Based or Emergency Assignment
                if (typeName.includes('skill') || typeName.includes('emergency')) {
                  return (
                                <div className="form-group form-group-full" key="skill-emergency-assignment">
                      <label>Special Requirements</label>
                      <textarea
                                    value={rule.assignmentNotes || ''}
                                    onChange={(e) => handleAssignmentChange(rule.id, 'assignmentNotes', e.target.value)}
                        rows={2}
                        placeholder={typeName.includes('skill') 
                          ? 'Describe required skills or competencies...' 
                          : 'Emergency assignment criteria...'}
                      />
                    </div>
                  );
                }
                
                return null;
              })()}

                          {rule.assignmentType && (
                <>
                  <div className="form-group">
                    <label>Preferred Shift</label>
                    <select
                                  value={rule.preferredShift || ''}
                                  onChange={(e) => handleAssignmentChange(rule.id, 'preferredShift', e.target.value || null)}
                    >
                      <option value="">Any Shift</option>
                      {shiftTypes && shiftTypes.map(shift => (
                        <option key={shift.id} value={shift.id}>{shift.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Max Concurrent Tasks</label>
                    <input
                      type="number"
                                  value={rule.workloadThreshold || 5}
                                  onChange={(e) => handleAssignmentChange(rule.id, 'workloadThreshold', parseInt(e.target.value) || 5)}
                      min="1"
                      max="20"
                    />
                  </div>
                </>
              )}
                          </div>
                        </div>
                        
                        <div className="rule-form-actions">
                          <button
                            type="button"
                            onClick={() => saveAssignmentRule(rule.id)}
                            className="btn btn-primary"
                            title="Save this rule"
                          >
                            <Save size={16} />
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (rule.saved) {
                                setEditingAssignmentRule(null);
                              } else {
                                removeAssignmentRule(rule.id);
                              }
                            }}
                            className="btn btn-secondary"
                            title="Cancel editing"
                          >
                            <X size={16} />
                            Cancel
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
            </>
            )}
        </div>

        {/* Scheduling */}
        <div className="form-section">
          <div 
            className="section-header-collapsible"
            onClick={() => setSchedulingExpanded(!schedulingExpanded)}
          >
            <h3>
              {t('protocols:sectionScheduling')}
              {formData.schedulingRules && formData.schedulingRules.length > 0 && (
                <span className="risk-count-badge">
                  {formData.schedulingRules.length}
                </span>
              )}
            </h3>
            <div className="section-header-actions">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  addSchedulingRule();
                }}
                className="btn btn-success section-add-btn"
              >
                <Plus size={16} />
                {t('protocols:addSchedulingRule')}
              </button>
              {schedulingExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>

          {schedulingExpanded && (
            <>
            {formData.schedulingRules.length === 0 && !editingSchedulingRule ? (
              <p className="no-items-message">
                {t('protocols:noSchedulingRulesYet')}
              </p>
            ) : (
              <div className="scheduling-rules">
                {/* Saved rules - compact view */}
                {formData.schedulingRules
                  .filter(rule => rule.saved && editingSchedulingRule !== rule.id)
                  .map((rule) => {
                    const r = normalizeLegacyRule(rule);
                    const scheduleTypeName = r.frequency === SCHEDULE_TYPE_RISK_DRIVEN ? t('protocols:scheduleTypeRiskDriven') : (frequencyTypes?.find(f => f.code === (r.frequency || ''))?.name || r.frequency || '—');
                    let detail = scheduleTypeName;
                    if (isRiskDrivenType(r)) {
                      const timeStr = (r.times && r.times.length) ? r.times.map(t => formatTimeSlot(t)).join(', ') : '—';
                      detail = `${scheduleTypeName} · ${t('protocols:recurrenceResolvedPerLocation')} @ ${timeStr}`;
                    } else if (isScheduledType(r)) {
                      const timeStr = (r.times && r.times.length) ? r.times.map(t => formatTimeSlot(t)).join(', ') : '—';
                      let extra = '';
                      if (r.recurrenceType === RECURRENCE.WEEKLY && r.daysOfWeek && r.daysOfWeek.length) {
                        extra = ` ${r.daysOfWeek.map(d => DAYS_OF_WEEK.find(x => x.value === d)?.label || d).join(', ')}`;
                      } else if (r.recurrenceType === RECURRENCE.MONTHLY) {
                        if (r.dayOfMonth != null) extra = ` day ${r.dayOfMonth}`;
                        else if (r.weekInMonth != null && r.dayOfWeek != null) extra = ` ${r.weekInMonth === 'last' ? 'last' : `${r.weekInMonth}st`} ${DAYS_OF_WEEK.find(x => x.value === r.dayOfWeek)?.label || ''}`;
                      } else if (r.recurrenceType === RECURRENCE.YEARLY && r.month != null && r.yearDayOfMonth != null) {
                        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        extra = ` ${months[Number(r.month) - 1]} ${r.yearDayOfMonth}`;
                      }
                      detail += ` · ${r.recurrenceType || 'daily'} @ ${timeStr}${extra}`;
                    }
                    if (r.timeConstraints && r.timeConstraints.maxDuration) detail += ` · max ${r.timeConstraints.maxDuration}min`;
                    const savedIndex = formData.schedulingRules.filter(rr => rr.saved).findIndex(rr => rr.id === rule.id);
                    return (
                      <div key={rule.id} className="scheduling-rule-item">
                        <div className="rule-info">
                          <span className="rule-number">Schedule {savedIndex + 1}</span>
                          <span className="rule-summary">{detail}</span>
                        </div>
                        <div className="rule-actions">
                          <button
                            type="button"
                            onClick={() => setEditingSchedulingRule(rule.id)}
                            className="btn btn-edit"
                            title="Edit this rule"
                          >
                            <Edit size={16} />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => removeSchedulingRule(rule.id)}
                            className="btn btn-delete"
                            title="Remove this rule"
                          >
                            <Trash2 size={16} />
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                
                {/* Editing/Adding rule - full form */}
                {formData.schedulingRules
                  .filter(rule => !rule.saved || editingSchedulingRule === rule.id)
                  .map((rule) => {
                    const r = normalizeLegacyRule(rule);
                    const times = (r.times && r.times.length) ? r.times : [{ hour: 8, minute: 0 }];
                    return (
                    <div key={rule.id} className="scheduling-rule-form">
                      <div className="rule-header">
                        <span className="rule-number">{rule.saved ? `Edit Schedule ${formData.schedulingRules.filter(rr => rr.saved).findIndex(rr => rr.id === rule.id) + 1}` : `New Schedule`}</span>
                      </div>
                      <div className="rule-content scheduling-compact">
                        <div className="scheduling-grid">
                          <div className="form-group">
                            <label>Schedule type</label>
                            <select
                              value={r.frequency || ''}
                              onChange={(e) => handleSchedulingRuleChange(rule.id, 'frequency', e.target.value)}
                            >
                              <option value="">Select schedule type...</option>
                              <option value={SCHEDULE_TYPE_RISK_DRIVEN}>{t('protocols:scheduleTypeRiskDriven')}</option>
                              {frequencyTypes && frequencyTypes.map(freq => (
                                <option key={freq.id} value={freq.code}>
                                  {freq.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          {isRiskDrivenType(r) && (
                            <>
                              <div className="form-group form-group-full scheduling-risk-driven-preview">
                                <p className="section-hint">{t('protocols:scheduleTypeRiskDrivenPreview')}</p>
                              </div>
                              <div className="form-group form-group-full">
                                <label>{t('protocols:timesOfDay')}</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                                  {times.map((t, idx) => {
                                    const h = t.hour != null ? t.hour : 8;
                                    const m = t.minute != null ? t.minute : 0;
                                    const timeValue = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                    return (
                                      <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input type="time" value={timeValue} onChange={(e) => handleSchedulingTimeSlotTimeInput(rule.id, idx, e.target.value)} style={{ padding: '6px 8px' }} />
                                        {times.length > 1 && (
                                          <button type="button" className="btn btn-delete btn-icon" onClick={() => removeSchedulingTimeSlot(rule.id, idx)} title="Remove time"><X size={14} /></button>
                                        )}
                                      </span>
                                    );
                                  })}
                                  <button type="button" className="btn btn-secondary" onClick={() => addSchedulingTimeSlot(rule.id)}>+ Add time</button>
                                </div>
                                <small>{t('protocols:timesOfDayHint24')}</small>
                              </div>
                            </>
                          )}
                          {isScheduledType(r) && (
                            <>
                          <div className="form-group">
                            <label>Recurrence</label>
                            <select
                              value={r.recurrenceType || RECURRENCE.DAILY}
                              onChange={(e) => handleSchedulingRuleChange(rule.id, 'recurrenceType', e.target.value)}
                            >
                              <option value={RECURRENCE.DAILY}>Daily</option>
                              <option value={RECURRENCE.WEEKLY}>Weekly</option>
                              <option value={RECURRENCE.MONTHLY}>Monthly</option>
                              <option value={RECURRENCE.YEARLY}>Yearly</option>
                            </select>
                          </div>
                          <div className="form-group form-group-full">
                            <label>Time(s) of day</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                              {times.map((t, idx) => {
                                const h = t.hour != null ? t.hour : 8;
                                const m = t.minute != null ? t.minute : 0;
                                const timeValue = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                return (
                                  <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <input type="time" value={timeValue} onChange={(e) => handleSchedulingTimeSlotTimeInput(rule.id, idx, e.target.value)} style={{ padding: '6px 8px' }} />
                                    {times.length > 1 && (
                                      <button type="button" className="btn btn-delete btn-icon" onClick={() => removeSchedulingTimeSlot(rule.id, idx)} title="Remove time"><X size={14} /></button>
                                    )}
                                  </span>
                                );
                              })}
                              <button type="button" className="btn btn-secondary" onClick={() => addSchedulingTimeSlot(rule.id)}>+ Add time</button>
                            </div>
                            <small>{t('protocols:timesOfDayHint24')}</small>
                          </div>
                          {r.recurrenceType === RECURRENCE.WEEKLY && (
                            <div className="form-group form-group-full">
                              <label>Day(s) of week</label>
                              <div className="checkbox-group-compact">
                                {DAYS_OF_WEEK.map(day => (
                                  <label key={day.value} className="checkbox-label-compact">
                                    <input
                                      type="checkbox"
                                      checked={(r.daysOfWeek || []).includes(day.value)}
                                      onChange={(e) => handleSchedulingDayOfWeek(rule.id, day.value, e.target.checked)}
                                    />
                                    <span>{day.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                          {r.recurrenceType === RECURRENCE.MONTHLY && (
                            <>
                              <div className="form-group">
                                <label>Or: Day of month (1–31)</label>
                                <input
                                  type="number"
                                  min={1}
                                  max={31}
                                  value={r.dayOfMonth ?? ''}
                                  onChange={(e) => handleSchedulingRuleChange(rule.id, 'dayOfMonth', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                  placeholder="e.g. 15"
                                />
                              </div>
                              <div className="form-group">
                                <label>Or: Week in month</label>
                                <select
                                  value={r.weekInMonth ?? ''}
                                  onChange={(e) => handleSchedulingRuleChange(rule.id, 'weekInMonth', e.target.value === '' ? null : (e.target.value === 'last' ? 'last' : parseInt(e.target.value, 10)))}
                                >
                                  <option value="">—</option>
                                  <option value={1}>1st</option>
                                  <option value={2}>2nd</option>
                                  <option value={3}>3rd</option>
                                  <option value={4}>4th</option>
                                  <option value="last">Last</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label>Day of week (for week-in-month)</label>
                                <select
                                  value={r.dayOfWeek ?? ''}
                                  onChange={(e) => handleSchedulingRuleChange(rule.id, 'dayOfWeek', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                >
                                  <option value="">—</option>
                                  {DAYS_OF_WEEK.map(day => (
                                    <option key={day.value} value={day.value}>{day.label}</option>
                                  ))}
                                </select>
                              </div>
                            </>
                          )}
                          {r.recurrenceType === RECURRENCE.YEARLY && (
                            <>
                              <div className="form-group">
                                <label>Month</label>
                                <select
                                  value={r.month ?? ''}
                                  onChange={(e) => handleSchedulingRuleChange(rule.id, 'month', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                >
                                  <option value="">—</option>
                                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                                    <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label>Day of month (1–31)</label>
                                <input
                                  type="number"
                                  min={1}
                                  max={31}
                                  value={r.yearDayOfMonth ?? ''}
                                  onChange={(e) => handleSchedulingRuleChange(rule.id, 'yearDayOfMonth', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                  placeholder="e.g. 15"
                                />
                              </div>
                            </>
                          )}
                          <div className="form-group">
                            <label>Max Duration (minutes)</label>
                            <input
                              type="number"
                              min={1}
                              value={(r.timeConstraints && r.timeConstraints.maxDuration) ?? 30}
                              onChange={(e) => handleSchedulingTimeConstraintChange(rule.id, 'maxDuration', parseInt(e.target.value, 10) || 30)}
                            />
                          </div>
                          <div className="form-group">
                            <label>Timeout Action</label>
                            <select
                              value={(r.timeConstraints && r.timeConstraints.timeoutAction) || 'escalate'}
                              onChange={(e) => handleSchedulingTimeConstraintChange(rule.id, 'timeoutAction', e.target.value)}
                            >
                              <option value="escalate">Escalate</option>
                              <option value="continue">Continue</option>
                              <option value="abort">Abort</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Escalation Contact</label>
                            <input
                              type="text"
                              value={(r.timeConstraints && r.timeConstraints.escalationContact) || ''}
                              onChange={(e) => handleSchedulingTimeConstraintChange(rule.id, 'escalationContact', e.target.value)}
                              placeholder="Contact for escalations"
                            />
                          </div>
                        </>
                      )}
                      {(isScheduledType(r) || isTriggeredType(r)) && (
                        <div className="form-group form-group-full">
                          <label>Trigger Events{isTriggeredType(r) ? '' : ' (optional)'}</label>
                          <div className="checkbox-group-compact">
                            {triggerEvents && triggerEvents.map(event => (
                              <label key={event.id} className="checkbox-label-compact">
                                <input
                                  type="checkbox"
                                  checked={(r.triggerEvents || []).includes(event.id)}
                                  onChange={(e) => handleSchedulingTriggerEvents(rule.id, event.id, e.target.checked)}
                                />
                                <span>{event.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                      
                      <div className="rule-form-actions">
                        <button
                          type="button"
                          onClick={() => {
                            saveSchedulingRule(rule.id);
                            setEditingSchedulingRule(null);
                          }}
                          className="btn btn-primary"
                          title="Save this rule"
                        >
                          <Save size={16} />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (rule.saved) {
                              setEditingSchedulingRule(null);
                            } else {
                              removeSchedulingRule(rule.id);
                            }
                          }}
                          className="btn btn-secondary"
                          title={rule.saved ? "Cancel editing" : "Cancel"}
                        >
                          <X size={16} />
                          Cancel
                        </button>
                      </div>
                    </div>
                    );
                  })}
              </div>
            )}
            </>
          )}
        </div>

        {/* Learning & Assessment (Învățare & Evaluare) — quiz PRD */}
        <div className="form-section">
          <div 
            className="section-header-collapsible"
            onClick={() => setLearningExpanded(!learningExpanded)}
          >
            <h3>{t('protocols:sectionLearningAssessment')}</h3>
            {learningExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          {learningExpanded && (
            <div className="learning-assessment-fields">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.learningAssessment?.generateQuiz === true}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      learningAssessment: { ...(prev.learningAssessment || {}), generateQuiz: e.target.checked }
                    }))}
                  />
                  {t('protocols:learningGenerateQuiz', 'Generate quiz with Anthropic (regenerates only when unchecked then rechecked)')}
                </label>
                <small className="form-hint">{t('protocols:learningGenerateQuizHint', 'When protocol is Active, a quiz is created for each assigned user group. To regenerate, uncheck and save, then check and save again.')}</small>
              </div>
              <div className="form-group">
                <label htmlFor="learningQuestionCount">{t('protocols:learningQuestionCount')}</label>
                <input
                  id="learningQuestionCount"
                  type="number"
                  min={5}
                  max={20}
                  value={formData.learningAssessment?.questionCount ?? 10}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) setFormData(prev => ({
                      ...prev,
                      learningAssessment: { ...(prev.learningAssessment || {}), questionCount: v }
                    }));
                  }}
                />
                <small className="form-hint">{t('protocols:learningQuestionCountHint')}</small>
                {errors.learningQuestionCount && <span className="error-text">{errors.learningQuestionCount}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="learningPassThreshold">{t('protocols:learningPassThreshold')}</label>
                <input
                  id="learningPassThreshold"
                  type="number"
                  min={50}
                  max={100}
                  value={formData.learningAssessment?.passThresholdPercent ?? 70}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) setFormData(prev => ({
                      ...prev,
                      learningAssessment: { ...(prev.learningAssessment || {}), passThresholdPercent: v }
                    }));
                  }}
                />
                <small className="form-hint">{t('protocols:learningPassThresholdHint')}</small>
                {errors.learningPassThreshold && <span className="error-text">{errors.learningPassThreshold}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="learningCooldown">{t('protocols:learningCooldown')}</label>
                <input
                  id="learningCooldown"
                  type="number"
                  min={5}
                  max={1440}
                  value={formData.learningAssessment?.cooldownMinutes ?? 60}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) setFormData(prev => ({
                      ...prev,
                      learningAssessment: { ...(prev.learningAssessment || {}), cooldownMinutes: v }
                    }));
                  }}
                />
                <small className="form-hint">{t('protocols:learningCooldownHint')}</small>
                {errors.learningCooldown && <span className="error-text">{errors.learningCooldown}</span>}
              </div>
            </div>
          )}
        </div>

        {/* Protocol Steps */}
        <div className="form-section">
          <div 
            className="section-header-collapsible"
            onClick={() => setStepsExpanded(!stepsExpanded)}
          >
            <h3>
              Protocol Steps
              {formData.steps && formData.steps.length > 0 && (
                <span className="risk-count-badge">
                  {formData.steps.length}
                </span>
              )}
            </h3>
            <div className="section-header-actions">
                <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddStep();
                }}
                className="btn btn-success section-add-btn"
                >
                  <Plus size={16} />
                {t('protocols:addStep')}
                </button>
              {stepsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
          </div>

          {stepsExpanded && (
            <>
              {errors.steps && (
                <span className="error-text">{errors.steps}</span>
              )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={formData.steps.map(s => s.id)}
              strategy={verticalListSortingStrategy}
            >
          <div className="steps-list">
            {formData.steps && formData.steps
              .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((step) => (
                    <SortableStepItem
                      key={step.id}
                      step={step}
                      onEdit={handleEditStep}
                      onDelete={handleDeleteStep}
                      t={t}
                    />
                  ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeId ? (
                <div className="step-item dragging">
                <div className="step-header">
                  <div className="step-info">
                      <span className="step-number">
                        {formData.steps.find(s => s.id === activeId)?.order || ''}
                      </span>
                      <span className="step-name">
                        {formData.steps.find(s => s.id === activeId)?.name || ''}
                      </span>
                  </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
            </>
          )}
        </div>

        {/* Evidence Base */}
        <div className="form-section">
          <div 
            className="section-header-collapsible"
            onClick={() => setEvidenceBaseExpanded(!evidenceBaseExpanded)}
          >
            <h3>
              {t('protocols:sectionEvidenceBase')}
              {((formData.evidence_base?.guidelines?.length || 0) + 
                (formData.evidence_base?.standards?.length || 0) + 
                (formData.evidence_base?.regulations?.length || 0)) > 0 && (
                <span className="risk-count-badge">
                  {(formData.evidence_base?.guidelines?.length || 0) + 
                   (formData.evidence_base?.standards?.length || 0) + 
                   (formData.evidence_base?.regulations?.length || 0)}
                </span>
              )}
            </h3>
            {evidenceBaseExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>

          {evidenceBaseExpanded && (
            <>
          <div className="evidence-base-section">
            <div className="evidence-base-header">
              <h4>Guidelines</h4>
                    <button
                      type="button"
                onClick={() => {
                  setGuidelineForm({ name: '', source: '', url: '', year: '' });
                  setShowGuidelineModal(true);
                }}
                className="btn btn-success evidence-base-btn"
              >
                <Plus size={16} />
                Add Guideline
                    </button>
            </div>
            <div className="evidence-list">
              {(formData.evidence_base?.guidelines || []).map((guideline, index) => (
                <div key={index} className="evidence-item">
                  <div className="evidence-item-content">
                    <div className="evidence-item-header">
                      <strong>{guideline.name}</strong>
                    <button
                      type="button"
                        onClick={() => {
                          const newGuidelines = [...(formData.evidence_base?.guidelines || [])];
                          newGuidelines.splice(index, 1);
                          handleInputChange('evidence_base', {
                            ...formData.evidence_base,
                            guidelines: newGuidelines
                          });
                        }}
                        className="btn btn-delete"
                        title="Remove guideline"
                      >
                        <Trash2 size={14} />
                        Remove
                    </button>
                  </div>
                    <div className="evidence-item-details">
                      <span>Source: {guideline.source}</span>
                      {guideline.year && <span>Year: {guideline.year}</span>}
                      {guideline.url && (
                        <a href={guideline.url} target="_blank" rel="noopener noreferrer">
                          View Source
                        </a>
                      )}
                </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="evidence-base-section">
            <div className="evidence-base-header">
              <h4>Standards</h4>
              <button
                type="button"
                onClick={() => {
                  setStandardForm({ name: '' });
                  setShowStandardModal(true);
                }}
                className="btn btn-success evidence-base-btn"
              >
                <Plus size={16} />
                Add Standard
              </button>
            </div>
            <div className="evidence-list">
              {(formData.evidence_base?.standards || []).map((standard, index) => (
                <div key={index} className="evidence-item">
                  <div className="evidence-item-content">
                    <div className="evidence-item-header">
                      <strong>{standard}</strong>
                      <button
                    type="button"
                    onClick={() => {
                      const newStandards = [...(formData.evidence_base?.standards || [])];
                      newStandards.splice(index, 1);
                      handleInputChange('evidence_base', {
                        ...formData.evidence_base,
                        standards: newStandards
                      });
                    }}
                    className="btn btn-delete"
                    title="Remove standard"
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                    </div>
                </div>
              </div>
            ))}
            </div>
          </div>

          <div className="evidence-base-section">
            <div className="evidence-base-header">
              <h4>Regulations</h4>
          <button
            type="button"
                onClick={() => {
                  setRegulationForm({ name: '' });
                  setShowRegulationModal(true);
                }}
                className="btn btn-success evidence-base-btn"
          >
            <Plus size={16} />
                Add Regulation
          </button>
            </div>
            <div className="evidence-list">
              {(formData.evidence_base?.regulations || []).map((regulation, index) => (
                <div key={index} className="evidence-item">
                  <div className="evidence-item-content">
                    <div className="evidence-item-header">
                      <strong>{regulation}</strong>
                      <button
                    type="button"
                    onClick={() => {
                      const newRegulations = [...(formData.evidence_base?.regulations || [])];
                      newRegulations.splice(index, 1);
                      handleInputChange('evidence_base', {
                        ...formData.evidence_base,
                        regulations: newRegulations
                      });
                    }}
                    className="btn btn-delete"
                    title="Remove regulation"
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </>
          )}
        </div>

        {/* Guideline Modal */}
        <Modal
          isOpen={showGuidelineModal}
          onClose={() => {
            setShowGuidelineModal(false);
            setGuidelineForm({ name: '', source: '', url: '', year: '' });
          }}
          title="Add Guideline"
          size="medium"
        >
          <div className="modal-form">
            <div className="form-group">
              <label htmlFor="guideline-name">Guideline Name *</label>
              <input
                type="text"
                id="guideline-name"
                value={guidelineForm.name}
                onChange={(e) => setGuidelineForm({ ...guidelineForm, name: e.target.value })}
                className="field-input"
                placeholder="e.g., WHO Hand Hygiene Guidelines"
              />
            </div>
            <div className="form-group">
              <label htmlFor="guideline-source">Source *</label>
              <input
                type="text"
                id="guideline-source"
                value={guidelineForm.source}
                onChange={(e) => setGuidelineForm({ ...guidelineForm, source: e.target.value })}
                className="field-input"
                placeholder="e.g., World Health Organization"
              />
            </div>
            <div className="form-group">
              <label htmlFor="guideline-url">URL (Optional)</label>
              <input
                type="url"
                id="guideline-url"
                value={guidelineForm.url}
                onChange={(e) => setGuidelineForm({ ...guidelineForm, url: e.target.value })}
                className="field-input"
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label htmlFor="guideline-year">Year (Optional)</label>
              <input
                type="number"
                id="guideline-year"
                value={guidelineForm.year}
                onChange={(e) => setGuidelineForm({ ...guidelineForm, year: e.target.value })}
                className="field-input"
                placeholder="e.g., 2024"
                min="1900"
                max="2100"
              />
            </div>
            <div className="form-actions">
          <button
            type="button"
                onClick={() => {
                  setShowGuidelineModal(false);
                  setGuidelineForm({ name: '', source: '', url: '', year: '' });
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (guidelineForm.name && guidelineForm.source) {
                    const newGuidelines = [
                      ...(formData.evidence_base?.guidelines || []),
                      {
                        name: guidelineForm.name,
                        source: guidelineForm.source,
                        url: guidelineForm.url || '',
                        year: guidelineForm.year ? parseInt(guidelineForm.year) : null
                      }
                    ];
                    handleInputChange('evidence_base', {
                      ...formData.evidence_base,
                      guidelines: newGuidelines
                    });
                    setShowGuidelineModal(false);
                    setGuidelineForm({ name: '', source: '', url: '', year: '' });
                  }
                }}
                className="btn btn-primary"
                disabled={!guidelineForm.name || !guidelineForm.source}
              >
                Add Guideline
          </button>
        </div>
          </div>
        </Modal>

        {/* Standard Modal */}
        <Modal
          isOpen={showStandardModal}
          onClose={() => {
            setShowStandardModal(false);
            setStandardForm({ name: '' });
          }}
          title="Add Standard"
          size="small"
        >
          <div className="modal-form">
            <div className="form-group">
              <label htmlFor="standard-name">Standard Name *</label>
              <input
                type="text"
                id="standard-name"
                value={standardForm.name}
                onChange={(e) => setStandardForm({ ...standardForm, name: e.target.value })}
                className="field-input"
                placeholder="e.g., ISO 14001"
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => {
                  setShowStandardModal(false);
                  setStandardForm({ name: '' });
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (standardForm.name.trim()) {
                    const newStandards = [
                      ...(formData.evidence_base?.standards || []),
                      standardForm.name.trim()
                    ];
                    handleInputChange('evidence_base', {
                      ...formData.evidence_base,
                      standards: newStandards
                    });
                    setShowStandardModal(false);
                    setStandardForm({ name: '' });
                  }
                }}
                className="btn btn-primary"
                disabled={!standardForm.name.trim()}
              >
                Add Standard
              </button>
            </div>
          </div>
        </Modal>

        {/* Regulation Modal */}
        <Modal
          isOpen={showRegulationModal}
          onClose={() => {
            setShowRegulationModal(false);
            setRegulationForm({ name: '' });
          }}
          title="Add Regulation"
          size="small"
        >
          <div className="modal-form">
            <div className="form-group">
              <label htmlFor="regulation-name">Regulation Name *</label>
              <input
                type="text"
                id="regulation-name"
                value={regulationForm.name}
                onChange={(e) => setRegulationForm({ ...regulationForm, name: e.target.value })}
                className="field-input"
                placeholder="e.g., EU Regulation 2017/745"
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => {
                  setShowRegulationModal(false);
                  setRegulationForm({ name: '' });
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (regulationForm.name.trim()) {
                    const newRegulations = [
                      ...(formData.evidence_base?.regulations || []),
                      regulationForm.name.trim()
                    ];
                    handleInputChange('evidence_base', {
                      ...formData.evidence_base,
                      regulations: newRegulations
                    });
                    setShowRegulationModal(false);
                    setRegulationForm({ name: '' });
                  }
                }}
                className="btn btn-primary"
                disabled={!regulationForm.name.trim()}
              >
                Add Regulation
              </button>
            </div>
          </div>
        </Modal>

        {/* Remove Step Confirmation Modal */}
        <Modal
          isOpen={showDeleteStepModal}
          onClose={() => {
            setShowDeleteStepModal(false);
            setStepToDelete(null);
          }}
          title="Remove Step"
          size="small"
        >
          <div className="modal-form">
            <p>Are you sure you want to remove "{stepToDelete?.name}"? This action cannot be undone.</p>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteStepModal(false);
                  setStepToDelete(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteStep}
                className="btn btn-delete"
              >
                Remove Step
              </button>
            </div>
          </div>
        </Modal>

        {/* Error Modal */}
        <Modal
          isOpen={showErrorModal}
          onClose={() => {
            setShowErrorModal(false);
            setErrorMessage(null);
          }}
          title="Error"
          size="small"
        >
          <div className="modal-form">
            <p>{errorMessage}</p>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage(null);
                }}
                className="btn btn-primary"
              >
                OK
              </button>
            </div>
          </div>
        </Modal>
      </div>
      </div>
    </div>
  );
};

export default ProtocolForm;
